import { FormControl, FormHelperText } from "@mui/material";
import { Button, Grid, MenuItem, Paper, Select, SelectChangeEvent, TextField } from "@mui/material";
import { Typography } from "@mui/material";
import { Box } from "@mui/system";
import { Key, useEffect } from "react";
import { useState } from "react";
import { toast } from "react-toastify";

import LoadingDialog from "../../components/LoadingDialog/LoadingDialog";
import { BooleanDictionary } from "../../GlobalTypes";
import { DeliveryAddressModel } from "../../models/DeliveryAddressModel";
import { PartnerModel } from "../../models/PartnerModel";
import ResponseModel from "../../models/ResponseModel";
import { TerminalModel } from "../../models/TerminalModel";
import { TerminalTypeModel } from "../../models/TerminalTypeModel";
import PartnerService from "../../services/partners/PartnerService";
import TerminalRequestService from "../../services/terminalRequests/TerminalRequestService";
import TerminalTypeService from "../../services/terminalTypes/TerminalTypeService";
import { doneChecker, getLongAddress, hasApiError } from "../../utils/Utils";

import { style } from "./CreateTerminalRequest.style";

type CreateTerminalRequestProps = {
  setDialogOpen: (value: boolean) => void;
  // from terminal request list
  getTerminalRequests?: () => void | Promise<void>;
  // from swap at terminals
  title?: string;
  swapTerminal?: TerminalModel;
};

type CreateTerminalRequestDone = BooleanDictionary & {
  partners: boolean;
  terminalTypes: boolean;
};

const CreateTerminalRequest = (props: CreateTerminalRequestProps): JSX.Element => {
  const [swapTerminal] = useState<TerminalModel | undefined>(props.swapTerminal);
  const [terminalTypes, setTerminalTypes] = useState<TerminalTypeModel[]>([]);
  const [partners, setPartners] = useState<PartnerModel[]>([]);
  const [deliveryAddresses, setDeliveryAddresses] = useState<DeliveryAddressModel[]>([]);
  const [amount, setAmount] = useState<string>("1");
  const [selectedTerminalType, setSelectedTerminalType] = useState<TerminalTypeModel | undefined>(undefined);
  const [selectedPartner, setSelectedPartner] = useState<PartnerModel | undefined>(undefined);
  const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState<DeliveryAddressModel | undefined>(undefined);
  const [done, setDone] = useState<CreateTerminalRequestDone>({ partners: false, terminalTypes: false });

  //getting all the terminal types
  const getTerminalTypes = async (): Promise<void> => {
    const response: ResponseModel = await TerminalTypeService.getTerminalTypes();

    //showing the result
    if (!hasApiError(response)) {
      setTerminalTypes(response.data as TerminalTypeModel[]);
    }

    if (!done.terminalTypes)
      setDone((prev: CreateTerminalRequestDone): CreateTerminalRequestDone => ({ ...prev, terminalTypes: true }));
  };

  //getting all the partners
  const getPartners = async (): Promise<void> => {
    const response: ResponseModel = await PartnerService.getPartners({});

    //showing the result
    if (!hasApiError(response)) {
      setPartners(response.data as PartnerModel[]);
    }

    if (!done.partners)
      setDone((prev: CreateTerminalRequestDone): CreateTerminalRequestDone => ({ ...prev, partners: true }));
  };

  //on partner update, set up the new addresses
  const getDeliveryAddresses = (): void => {
    setDeliveryAddresses(
      partners.find((partner: PartnerModel): boolean => partner.partnerCode === selectedPartner?.partnerCode)
        ?.deliveryAddresses as DeliveryAddressModel[],
    );
  };

  //partner select handler
  const onPartnerSelect = (e: SelectChangeEvent): void => {
    setSelectedPartner(
      partners.find(
        (partner: PartnerModel): boolean => partner.partnerCode === parseInt(e.target.value),
      ) as PartnerModel,
    );
    setSelectedDeliveryAddress(undefined);
  };

  //address select handler
  const onDeliveryAddressSelect = (e: SelectChangeEvent): void => {
    setSelectedDeliveryAddress(
      deliveryAddresses.find(
        (address: DeliveryAddressModel): boolean => address.deliveryAddressCode === parseInt(e.target.value),
      ) as DeliveryAddressModel,
    );
  };

  //terminal select handler
  const onTerminalTypeSelect = (e: SelectChangeEvent): void => {
    setSelectedTerminalType(
      terminalTypes.find(
        (terminal: TerminalTypeModel): boolean => terminal.terminalTypeCode === parseInt(e.target.value),
      ) as TerminalTypeModel,
    );
  };

  //amount change handler
  const onAmountChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setAmount(event.target.value);
  };

  //event for registration button
  const onRegisterButton = async (): Promise<void> => {
    // guards
    if (swapTerminal === undefined) {
      if (selectedDeliveryAddress === undefined) {
        toast.error("Nem adtál meg szállítási címet");
        return;
      }
      if (selectedTerminalType === undefined) {
        toast.error("Nem adtál meg típust");
        return;
      }
      if (isNaN(parseInt(amount)) || parseInt(amount).toString() !== amount) {
        toast.error("Nem valós egész mennyiséget adtál meg");
        return;
      }
      if (parseInt(amount) < 1) {
        toast.error("Pozitív kell legyen a mennyiség");
        return;
      }
    }

    // data
    let data: Record<string, unknown>;
    // swapping request
    if (swapTerminal !== undefined) {
      data = {
        deliveryAddressCode: swapTerminal.tid?.deliveryAddressCode ?? 0,
        terminalTypeCode: swapTerminal.tid?.terminalTypeId ?? 0,
        number: 1,
        tidCode: swapTerminal.tidCode,
      };
    }
    // adding new requests
    else {
      data = {
        deliveryAddressCode: selectedDeliveryAddress?.deliveryAddressCode ?? 0,
        terminalTypeCode: selectedTerminalType?.terminalTypeCode ?? 0,
        number: parseInt(amount),
      };
    }

    // send the request
    const loadingToastId = toast.loading("Igény leadása folyamatban...");
    const response: ResponseModel = await TerminalRequestService.addTerminalRequest(data);

    // showing the result
    if (hasApiError(response)) {
      toast.dismiss(loadingToastId);
      return;
    } else {
      toast.update(loadingToastId, { render: "Sikeres igény leadás", type: "success", isLoading: false, autoClose: 4000 });
      props.setDialogOpen(false);
      if (props.getTerminalRequests) void props.getTerminalRequests();
    }
  };

  //get data on load
  useEffect((): void => {
    void getTerminalTypes();
    void getPartners();
  }, []);

  //reload the addresses when the partner changes
  useEffect((): void => {
    if (selectedPartner !== undefined) {
      getDeliveryAddresses();
    } else {
      setSelectedDeliveryAddress(undefined);
      setDeliveryAddresses([]);
    }
  }, [selectedPartner]);

  // blocking with a dialog while getting all the data
  if (!doneChecker(done)) {
    return <LoadingDialog />;
  }

  return (
    <Paper style={style.paper}>
      <Typography variant="h3" align="center" marginBottom={8}>
        {props.title}
      </Typography>
      <Grid container spacing={4}>
        {/* Partner select */}
        <Grid item sm={6} md={3} xs={12}>
          <Typography style={style.label}>Ügyfél</Typography>
          <Select
            fullWidth
            value={
              swapTerminal
                ? (swapTerminal.tid?.deliveryAddress?.partnerCode?.toString() ?? "-1")
                : (selectedPartner?.partnerCode.toString() ?? "-1")
            }
            disabled={swapTerminal !== undefined}
            onChange={onPartnerSelect}
          >
            <MenuItem value={"-1"} key={-1}>
              Nincs kiválasztva
            </MenuItem>
            {partners.map((partner: PartnerModel, i: Key): JSX.Element => {
              return (
                <MenuItem value={partner.partnerCode} key={i}>
                  {partner.name}
                </MenuItem>
              );
            })}
          </Select>
        </Grid>

        {/* Delivery Address select */}
        <Grid item sm={6} md={3} xs={12}>
          <Typography style={style.label}>Telephely</Typography>
          <FormControl style={style.formControl}>
            <Select
              fullWidth
              defaultValue={"-1"}
              onChange={onDeliveryAddressSelect}
              value={
                swapTerminal
                  ? (swapTerminal.tid?.deliveryAddressCode?.toString() ?? "-1")
                  : (selectedDeliveryAddress?.deliveryAddressCode.toString() ?? "-1")
              }
              disabled={swapTerminal !== undefined}
            >
              <MenuItem value={"-1"} key={-1}>
                Nincs kiválasztva
              </MenuItem>
              {swapTerminal !== undefined && swapTerminal.tid?.deliveryAddress && (
                <MenuItem value={swapTerminal.tid.deliveryAddressCode ?? ""}>
                  {getLongAddress(swapTerminal.tid.deliveryAddress, true)}
                </MenuItem>
              )}
              {deliveryAddresses.map((address: DeliveryAddressModel, i: Key): JSX.Element => {
                return (
                  <MenuItem value={address.deliveryAddressCode} key={i}>
                    {getLongAddress(address, true)}
                  </MenuItem>
                );
              })}
            </Select>
            <FormHelperText>Először válassz ügyfélt</FormHelperText>
          </FormControl>
        </Grid>

        {/* Terminal type */}
        <Grid item sm={6} md={3} xs={12}>
          <Typography style={style.label}>Terminál típusa</Typography>
          <Select
            fullWidth
            disabled={swapTerminal !== undefined}
            value={
              swapTerminal
                ? (swapTerminal.tid?.terminalTypeId?.toString() ?? "-1")
                : (selectedTerminalType?.terminalTypeCode.toString() ?? "-1")
            }
            onChange={onTerminalTypeSelect}
          >
            <MenuItem value={"-1"} key={-1}>
              Nincs kiválasztva
            </MenuItem>
            {terminalTypes.map((type: TerminalTypeModel, i: Key): JSX.Element => {
              return (
                <MenuItem value={type.terminalTypeCode} key={i}>
                  {type.name}
                </MenuItem>
              );
            })}
          </Select>
        </Grid>

        {/* Number of requests */}
        <Grid item sm={6} md={3} xs={12}>
          <Typography style={style.label}>Igényelt terminál darabszáma</Typography>
          <TextField
            fullWidth
            type="number"
            disabled={swapTerminal !== undefined}
            value={swapTerminal ? 1 : amount}
            onChange={onAmountChange}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
      </Grid>

      {/* Buttons */}
      <Box style={style.buttonBox}>
        <Button variant="contained" color="error" onClick={(): void => props.setDialogOpen(false)}>
          Vissza
        </Button>
        <Button
          variant="contained"
          onClick={(): void => {
            void onRegisterButton();
          }}
          style={style.button}
        >
          Mentés
        </Button>
      </Box>
    </Paper>
  );
};

export default CreateTerminalRequest;
